import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ApolloError } from '@apollo/client';

import { getApplicationError, getValidationErrors } from '@oysterjs/core/graphql/errors';

import { client } from '~/api/client';
import {
  CreateCaraWorkspaceDocument,
  CreateCaraWorkspaceMutation,
  type CaraWorkspaceFileInput
} from '~/api/graphql';
import {
  visibilityInputFromValue,
  type VisibilitySelectorValue
} from '~/components/visibility/visibility-selector.utils';
import { useVisibilityEntitlement } from '~/components/visibility/use-visibility-entitlement';
import {
  type CopilotActionError,
  copilotActionErrorFromApplicationError
} from '~/lib/copilot-action-error';
import { type CopilotWorkspaceCreateOptions } from '~/components/presets/copilot-launcher';

/**
 * Shared workspace creation, including navigation and errors. `defaultVisibility` is the
 * compose selection, used when a caller does not pass one. `onLaunched` runs once a
 * workspace exists, just before navigating to it.
 */
export const useCreateCaraWorkspace = (
  defaultVisibility: VisibilitySelectorValue,
  hooks?: { onLaunched?: () => void }
) => {
  const navigate = useNavigate();
  const visibilityEnabled = useVisibilityEntitlement();

  // A ref, not state: two rapid clicks would both observe `loading === false` on
  // the render they were dispatched from and create two workspaces.
  const loadingRef = React.useRef(false);
  const [error, setError] = React.useState<CopilotActionError>();
  const clearError = React.useCallback(() => setError(undefined), []);

  const createWorkspace = async (
    query: string,
    files: CaraWorkspaceFileInput[],
    options?: CopilotWorkspaceCreateOptions
  ) => {
    const {
      documentIds,
      presetOverride,
      customFormIds,
      htmlDocumentTemplateIds,
      wordDocumentTemplateIds,
      excelSpreadsheetTemplateIds,
      omitPresetOutputTemplates,
      visibility,
      outputFormat,
      effortLevel,
      useCase,
      clientId,
      amsContext,
      amsRefs
    } = options ?? {};
    if (loadingRef.current) {
      return;
    }

    loadingRef.current = true;
    setError(undefined);

    try {
      const uploadFiles = files.filter((f) => 'upload' in f && f.upload).map((f) => f.upload!);
      const existingFiles = files
        .filter((f) => 'existing' in f && f.existing)
        .map((f) => f.existing!);

      const res = await client.mutate<CreateCaraWorkspaceMutation>({
        mutation: CreateCaraWorkspaceDocument,
        variables: {
          input: {
            message: query.trim(),
            files: uploadFiles,
            existingFiles: existingFiles.length > 0 ? existingFiles : undefined,
            preset: presetOverride,
            documentIds: documentIds || [],
            customFormIds: customFormIds?.length ? customFormIds : undefined,
            htmlDocumentTemplateIds: htmlDocumentTemplateIds?.length
              ? htmlDocumentTemplateIds
              : undefined,
            wordDocumentTemplateIds: wordDocumentTemplateIds?.length
              ? wordDocumentTemplateIds
              : undefined,
            excelSpreadsheetTemplateIds: excelSpreadsheetTemplateIds?.length
              ? excelSpreadsheetTemplateIds
              : undefined,
            omitPresetOutputTemplates: omitPresetOutputTemplates ? true : undefined,
            outputFormat,
            effortLevel,
            useCase,
            clientId,
            visibility: visibilityEnabled
              ? visibilityInputFromValue(visibility ?? defaultVisibility)
              : undefined,
            presetMetadata: options?.presetMetadata ?? undefined,
            amsContext: amsContext?.length ? amsContext : undefined,
            amsRefs: amsRefs?.length ? amsRefs : undefined
          }
        }
      });

      const applicationError = getApplicationError(new ApolloError({ graphQLErrors: res.errors }));
      const validationErrors = getValidationErrors(new ApolloError({ graphQLErrors: res.errors }));

      if (applicationError) {
        const actionError = copilotActionErrorFromApplicationError(applicationError);
        setError(actionError);
        throw new Error(actionError.message);
      }
      if (validationErrors?.length) {
        const msg = validationErrors[0].message;
        setError({ kind: 'generic', message: msg });
        throw new Error(msg);
      }
      if (res.errors?.length) {
        const msg = res.errors[0].message;
        setError({ kind: 'generic', message: msg });
        throw new Error(msg);
      }
      if (res.data?.createCaraWorkspace?.id) {
        const workspaceId = res.data.createCaraWorkspace.id;
        setError(undefined);
        hooks?.onLaunched?.();
        navigate(`/app/workspaces/${workspaceId}`);
      } else {
        throw new Error('Failed to create workspace');
      }
    } catch (e: unknown) {
      setError((current) => current ?? { kind: 'generic', message: 'Failed to create workspace' });
      // eslint-disable-next-line no-console
      console.error(e);
      if (e instanceof Error) {
        throw e;
      }
      throw new Error('Failed to create workspace');
    } finally {
      loadingRef.current = false;
    }
  };

  return { createWorkspace, error, clearError };
};
